import { revalidateAllApiRoutes } from "../cache/revalidate";
import type {
  ApiCollectionResource,
  ApiCollectionResourceDefinition,
  ApiDirectResource,
  ApiDirectResourceDefinition,
  ApiInvalidations,
  ApiInvalidationTarget,
  ApiMutationMethod,
  ApiResourceDescriptor,
} from "./types";
import {
  createCollectionResourceRoute,
  createDetailResourceRoute,
} from "./resourceRoute";

type InvalidationDefinition<TRouteArgs extends readonly string[]> =
  | readonly ApiInvalidationTarget[]
  | ((...args: TRouteArgs) => readonly ApiInvalidationTarget[])
  | undefined;

function resolveInvalidation<TRouteArgs extends readonly string[]>(
  definition: InvalidationDefinition<TRouteArgs>,
  args?: TRouteArgs,
) {
  if (definition == null) return;
  if (typeof definition !== "function") return definition;
  if (args == null) return;
  return definition(...args);
}

function resolveCollectionInvalidations<
  TEntity,
  TCollectionArgs extends readonly string[],
>(
  definitions: ApiCollectionResourceDefinition<
    TEntity,
    TCollectionArgs
  >["invalidates"],
  collectionArgs: TCollectionArgs,
  detailArgs?: [...TCollectionArgs, id: string],
): ApiInvalidations | undefined {
  if (definitions == null) return;

  const invalidations: Partial<
    Record<ApiMutationMethod, readonly ApiInvalidationTarget[]>
  > = {};
  const post = resolveInvalidation(definitions.post, collectionArgs);
  const put = resolveInvalidation(definitions.put, detailArgs);
  const deletion = resolveInvalidation(definitions.delete, detailArgs);

  if (post?.length) invalidations.post = post;
  if (put?.length) invalidations.put = put;
  if (deletion?.length) invalidations.delete = deletion;
  if (!Object.keys(invalidations).length) return;

  return invalidations;
}

function createDirectResource<TEntity, TRouteArgs extends readonly string[]>(
  definition: ApiDirectResourceDefinition<TEntity, TRouteArgs>,
): ApiDirectResource<TEntity, TRouteArgs> {
  const { domainUrl, detailRoute, ...descriptor } = definition;

  function detail(...args: TRouteArgs) {
    return createDetailResourceRoute({
      route: detailRoute(...args),
      descriptor,
    });
  }

  return { ...descriptor, ...createDomainRevalidation(domainUrl), detail };
}

function createCollectionResource<
  TEntity,
  TCollectionArgs extends readonly string[],
>(
  definition: ApiCollectionResourceDefinition<TEntity, TCollectionArgs>,
): ApiCollectionResource<TEntity, TCollectionArgs> {
  const {
    domainUrl,
    collectionRoute,
    detailRoute,
    invalidates: invalidationDefinitions,
    ...descriptor
  } = definition;

  function resolveRouteDescriptor(invalidates?: ApiInvalidations) {
    let routeDescriptor: ApiResourceDescriptor<TEntity> = descriptor;
    if (invalidates != null) {
      routeDescriptor = { ...descriptor, invalidates };
    }

    return routeDescriptor;
  }

  function collection(...args: TCollectionArgs) {
    const resolvedCollectionRoute = collectionRoute(...args);
    const routeDescriptor = resolveRouteDescriptor(
      resolveCollectionInvalidations(invalidationDefinitions, args),
    );
    return createCollectionResourceRoute({
      route: resolvedCollectionRoute,
      collectionRoute: resolvedCollectionRoute,
      descriptor: routeDescriptor,
    });
  }

  function detail(...args: [...TCollectionArgs, id: string]) {
    const collectionArgs = args.slice(0, -1) as unknown as TCollectionArgs;
    const routeDescriptor = resolveRouteDescriptor(
      resolveCollectionInvalidations(
        invalidationDefinitions,
        collectionArgs,
        args,
      ),
    );
    return createDetailResourceRoute({
      route: detailRoute(...args),
      collectionRoute: collectionRoute(...collectionArgs),
      descriptor: routeDescriptor,
    });
  }

  return {
    ...descriptor,
    ...createDomainRevalidation(domainUrl),
    collection,
    detail,
  };
}

function createDomainRevalidation(domainUrl: string) {
  // Deliberately match the raw route prefix across cache tags and client types.
  async function revalidate() {
    await revalidateAllApiRoutes(domainUrl);
  }

  return { domainUrl, revalidate };
}

function isCollectionResourceDefinition<
  TEntity,
  TRouteArgs extends readonly string[],
>(
  definition:
    | ApiDirectResourceDefinition<TEntity, TRouteArgs>
    | ApiCollectionResourceDefinition<TEntity, TRouteArgs>,
): definition is ApiCollectionResourceDefinition<TEntity, TRouteArgs> {
  return definition.getEntityKey != null;
}

export function defineApiResource<
  TEntity,
  TRouteArgs extends readonly string[] = [],
>(
  definition: ApiDirectResourceDefinition<TEntity, TRouteArgs>,
): ApiDirectResource<TEntity, TRouteArgs>;

export function defineApiResource<
  TEntity,
  TCollectionArgs extends readonly string[] = [],
>(
  definition: ApiCollectionResourceDefinition<TEntity, TCollectionArgs>,
): ApiCollectionResource<TEntity, TCollectionArgs>;

export function defineApiResource<
  TEntity,
  TRouteArgs extends readonly string[] = [],
>(
  definition:
    | ApiDirectResourceDefinition<TEntity, TRouteArgs>
    | ApiCollectionResourceDefinition<TEntity, TRouteArgs>,
):
  | ApiDirectResource<TEntity, TRouteArgs>
  | ApiCollectionResource<TEntity, TRouteArgs> {
  if (isCollectionResourceDefinition(definition)) {
    return createCollectionResource(definition);
  }

  return createDirectResource(definition);
}
